import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import {
  BUTTON_THEMES,
  CARD_TYPES,
  CONTENTFUL_COMPONENT,
} from '../../../shared/constants';
import { ICard } from '../../../types/shared';

import Paper from '../../atoms/paper/Paper';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import TagList from '../../atoms/tag-list/TagList';

import {
  formatDateLocaleString,
  getInternalLink,
  ReactMarkdownExtended,
} from '../../../utils/generic';
import { getTagsByTypes } from '../../../utils/tags';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { Body, BodyCSS, H1, H2, H3, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { CardVariants, TagTypes, Widths } from '../../../tokens';
import { ArticleBodyVariants } from '../../../tokens/Articles';
import ImageHeader from '../../molecules/image-header/ImageHeader';
import LinkList from '../../molecules/link-list/LinkList';
import QuoteItem from '../../molecules/quote-item/QuoteItem';
import { ArticleBody } from '../../pages/article-page/ArticlePage.types';

import { ArticleDateTime, ArticleLayoutProps } from './ArticleLayout.props';

const ArticleLayout = ({
  dateTime,
  header,
  title,
  tags,
  body,
}: ArticleLayoutProps) => {
  const filteredTags = getTagsByTypes(tags, [
    TagTypes.SECTOR,
    TagTypes.CATEGORY,
  ]);

  return (
    <LayoutWrapper data-name="article-layout">
      <ImageHeader image={header.image} />
      <StyledPaper isElevated={false}>
        <StyledH1>{title}</StyledH1>
        {!!filteredTags.length && <StyledTagList tags={filteredTags} />}
        <DateTime {...dateTime} />
        {body.map((bodyItem, index) => renderSection(bodyItem, index))}
      </StyledPaper>
    </LayoutWrapper>
  );
};

const renderSection = (item: ArticleBody | ICard, index: number) => {
  switch (item.__typename) {
    case CONTENTFUL_COMPONENT.textSection:
      return <TextSection {...(item as ArticleBody)} key={index} />;
    case CONTENTFUL_COMPONENT.card:
      return renderCard(item as ICard, index);
    default:
      return undefined;
  }
};

const renderCard = (card: ICard, index: number) => {
  switch (card.type) {
    case CARD_TYPES.sales:
      return <SalesSection {...card} key={index} />;
    case CARD_TYPES.quote:
      return (
        <QuoteItem
          key={index}
          title={card.title || ''}
          subtitle={card.subtitle || ''}
          image={card.image}
        >
          <ReactMarkdownExtended>
            {card.body.childMarkdownRemark?.rawMarkdownBody}
          </ReactMarkdownExtended>
        </QuoteItem>
      );
    default:
      return undefined;
  }
};

const DateTime = ({ publishedAt, updatedAt, readingTime }: ArticleDateTime) => {
  const formattedPublishDate = formatDateLocaleString(new Date(publishedAt));
  const formattedUpdatedDate = formatDateLocaleString(new Date(updatedAt));

  return (
    <DateTimeText>
      <Body>
        <span>Publicatie: {formattedPublishDate}</span> •{' '}
        <span>{readingTime} min lezen</span>
      </Body>
      <span>Aangepast: {formattedUpdatedDate}</span>
    </DateTimeText>
  );
};

const TextSection = ({ variant, body, links, image }: ArticleBody) => {
  const markdownComponents = {
    h2: StyledH2,
    p: StyledP,
    ul: StyledUL,
    li: StyledLI,
  };

  const textSectionContent = (
    <>
      {body ? (
        <ReactMarkdownExtended components={markdownComponents}>
          {body.childMarkdownRemark?.rawMarkdownBody}
        </ReactMarkdownExtended>
      ) : undefined}
      {links ? (
        <StyledLinkList links={links} deq={{ componentType: 'ArticleLink' }} />
      ) : undefined}
      {image?.gatsbyImageData ? (
        <>
          <GatsbyImage
            image={image.gatsbyImageData}
            alt={image.description || ''}
          />
          {image.description ? (
            <StyledSmallText>{image.description}</StyledSmallText>
          ) : undefined}
        </>
      ) : undefined}
    </>
  );

  return (
    <StyledSection>
      {variant === ArticleBodyVariants.HIGHLIGHT ? (
        <HighlightPaper>{textSectionContent}</HighlightPaper>
      ) : (
        textSectionContent
      )}
    </StyledSection>
  );
};

const SalesSection = ({ variant, title, subtitle, primaryLink }: ICard) => (
  <StyledSection>
    <SalesPaper isElevated={false} variant={variant}>
      {title && <SalesH1>{title}</SalesH1>}
      {subtitle && <SalesH3>{subtitle}</SalesH3>}
      {primaryLink && (
        <SalesPrimaryButton
          type={primaryLink.type}
          label={primaryLink.label}
          to={
            primaryLink.linkedPage
              ? `/${getInternalLink(primaryLink.linkedPage)}`
              : primaryLink.externalUrl || primaryLink.asset?.file.url || ''
          }
          icon={primaryLink.icon}
          buttonTheme={
            primaryLink.theme ? primaryLink.theme : BUTTON_THEMES.highlight
          }
          deqComponentTitle={title}
        />
      )}
    </SalesPaper>
  </StyledSection>
);

interface SalesPaperProps {
  variant?: CardVariants;
}

const SalesPaper = styled(Paper)<SalesPaperProps>`
  align-items: center;
  background-color: ${({ variant }) =>
    variant === CardVariants.HIGHLIGHT ? colors.abnGreen500 : colors.white};
  color: ${({ variant }) =>
    variant === CardVariants.HIGHLIGHT ? colors.white : colors.abnNeutral670};
  display: flex;
  flex-direction: column;
`;

const SalesH1 = styled(H1)`
  margin: 0;
  text-align: center;
`;

const SalesH3 = styled(H3)`
  margin: ${Spacings.XS} 0 0;
  text-align: center;
`;

const SalesPrimaryButton = styled(PrimaryButton)`
  margin: ${Spacings.M} 0 0;
`;

const LayoutWrapper = styled.div`
  background-color: ${colors.white};
`;

const StyledPaper = styled(Paper)`
  margin: 0 auto;
  max-width: ${Widths.CONTENT_MAX};
  position: relative;

  ${mq.from.breakpoint.M`
    margin-top: -${Spacings.XXL};
  `}

  ${mq.from.breakpoint.L`
    margin-top: -${Spacings.XXXL};
  `}
`;

const HighlightPaper = styled(Paper)`
  background-color: ${colors.abnInverse};
`;

const StyledSection = styled.section`
  margin-bottom: ${Spacings.XL};
`;

const StyledH2 = styled(H2)`
  margin: ${Spacings.S} 0;

  &:first-child {
    margin: 0 0 ${Spacings.S};
  }
`;

const StyledP = styled.p`
  margin: ${Spacings.S} 0;

  &:first-child {
    margin: 0 0 ${Spacings.S};
  }

  &:last-child {
    margin: ${Spacings.S} 0 0;
  }
`;

const StyledH1 = styled(H1)`
  margin-bottom: ${Spacings.M};
`;

const StyledSmallText = styled(SmallText)`
  color: ${colors.abnNeutral400};
  margin-top: ${Spacings.XS};
`;

const StyledLinkList = styled(LinkList)`
  margin: ${Spacings.S} 0;

  &:first-child {
    margin: 0 0 ${Spacings.S};
  }

  &:last-child {
    margin: ${Spacings.S} 0 0;
  }
`;

const StyledTagList = styled(TagList)`
  margin-bottom: ${Spacings.M};
`;

const DateTimeText = styled.time`
  ${BodyCSS}
  color: ${colors.abnGrey620};
  display: block;
  margin-bottom: ${Spacings.XL};
`;

const StyledUL = styled.ul`
  list-style: none;
  margin: ${Spacings.S} 0;
  padding: 0;

  &:first-child {
    margin: 0 0 ${Spacings.S};
  }

  &:last-child {
    margin: ${Spacings.S} 0 0;
  }
`;

const StyledLI = styled.li`
  padding-left: ${Spacings.S};
  position: relative;

  &:before {
    color: ${colors.abnGreen500};
    content: '•';
    left: 0;
    position: absolute;
  }
`;

export default ArticleLayout;
